import React, { useEffect, useState } from 'react';
import { requester } from 'App';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import Dummy from 'components/Dummy';
import P from 'components/P';
import { AuthContext } from "providers/AuthProvider";
import { useContext } from 'react';
import SimpleStoreDetailModal from "modals/store/SimpleStoreDetailModal";
import { devLog } from "utils/logger";


function StoreMap() {
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [map, setMap] = useState(null);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [locationError, setLocationError] = useState(false);
    const [statusMessage, setStatusMessage] = useState('지도를 불러오는 중...');
    const { id } = useParams();
    const navigate = useNavigate();
    const { phoneNumber } = useContext(AuthContext);
    const [userLocation, setUserLocation] = useState(null); // 사용자의 현재 위치를 저장

    const createPulsingAnimation = (mapInstance, center) => {
        // 기본 반경 크기 대폭 축소
        const baseInnerRadius = 10; // 20 -> 10
        const baseOuterRadius = 30; // 70 -> 30

        // 현재 지도 레벨에 따라 원의 반경을 조정하는 함수 (변화율 최소화)
        const calculateRadius = (baseRadius) => {
            const level = mapInstance.getLevel();
            // 거의 고정값에 가깝게 만들기 위해 변화율을 매우 낮게 설정 (1.05)
            return baseRadius * Math.pow(1.05, level - 3);
        };


        // 내부 원
        const innerCircle = new window.kakao.maps.Circle({
            center: center,
            radius: calculateRadius(baseInnerRadius),
            strokeWeight: 0,
            strokeColor: 'var(--main-color)',
            strokeOpacity: 0,
            strokeStyle: 'solid',
            fillColor: 'var(--main-color)',
            fillOpacity: 0.8,
            map: mapInstance
        });

        // 외부 원
        const outerCircle = new window.kakao.maps.Circle({
            center: center,
            radius: calculateRadius(baseOuterRadius),
            strokeWeight: 1,
            strokeColor: 'var(--main-color)',
            strokeOpacity: 0.2,
            strokeStyle: 'solid',
            fillColor: 'var(--main-color)',
            fillOpacity: 0.1,
            map: mapInstance
        });

        // 중앙 점 (크기 축소)
        const centerDot = new window.kakao.maps.Circle({
            center: center,
            radius: calculateRadius(3), // 5 -> 3
            strokeWeight: 0,
            fillColor: '#FFFFFF',
            fillOpacity: 1,
            map: mapInstance
        });

        // 애니메이션 효과 (애니메이션 범위 축소)
        let radius = calculateRadius(baseOuterRadius);
        let growing = false;
        let maxRadius = calculateRadius(baseOuterRadius * 1.15); // 1.2 -> 1.15
        let minRadius = calculateRadius(baseOuterRadius);

        const updateAnimation = () => {
            if (growing) {
                radius += calculateRadius(0.5); // 1 -> 0.5 (애니메이션 속도 감소)
                if (radius >= maxRadius) growing = false;
            } else {
                radius -= calculateRadius(0.5); // 1 -> 0.5 (애니메이션 속도 감소)
                if (radius <= minRadius) growing = true;
            }

            outerCircle.setRadius(radius);
            outerCircle.setOptions({
                fillOpacity: 0.1 * (1 - (radius - minRadius) / (maxRadius - minRadius)),
                strokeOpacity: 0.2 * (1 - (radius - minRadius) / (maxRadius - minRadius))
            });
        };

        const animationInterval = setInterval(updateAnimation, 60); // 애니메이션 속도 조정 (50 -> 60ms)

        // 지도 확대/축소 이벤트 감지
        const zoomChangedHandler = function () {
            // 지도 레벨에 따라 원의 반경 재조정
            innerCircle.setRadius(calculateRadius(baseInnerRadius));
            centerDot.setRadius(calculateRadius(3)); // 5 -> 3

            // 애니메이션 관련 변수 업데이트
            minRadius = calculateRadius(baseOuterRadius);
            maxRadius = calculateRadius(baseOuterRadius * 1.15); // 1.2 -> 1.15
            radius = growing ?
                Math.min(radius, maxRadius) :
                Math.max(radius, minRadius);

            outerCircle.setRadius(radius);
        };

        // 지도 확대/축소 이벤트 리스너 등록
        window.kakao.maps.event.addListener(mapInstance, 'zoom_changed', zoomChangedHandler);

        // 컴포넌트 언마운트 시 정리할 수 있도록 정리 함수 반환
        return () => {
            clearInterval(animationInterval);
            window.kakao.maps.event.removeListener(mapInstance, 'zoom_changed', zoomChangedHandler);
            innerCircle.setMap(null);
            outerCircle.setMap(null);
            centerDot.setMap(null);
        };
    };

    // 지도 초기화 함수 수정
    const initializeMap = (container, center) => {
        try {
            // 컴포넌트가 마운트된 상태인지 확인
            if (window.storeMapUnmounted) return;

            // container가 없으면 컴포넌트가 언마운트된 것으로 간주
            if (!container) {
                devLog('map 요소가 존재하지 않아 지도 초기화를 중단합니다.');
                return;
            }

            // 지도가 이미 초기화되어 있는 경우 중복 초기화 방지
            if (map) {
                setStatusMessage('');
                setIsLoading(false);
                return;
            }

            const mapInstance = new window.kakao.maps.Map(container, {
                center,
                level: 5
            });

            // 상태 메시지 초기화와 로딩 상태 변경
            setStatusMessage('');
            setIsLoading(false);

            // map 상태 및 사용자 위치 업데이트
            setMap(mapInstance);
            setUserLocation(center);

            // 현재 위치 표시 애니메이션 
            const cleanupAnimation = createPulsingAnimation(mapInstance, center);
            window.cleanupLocationAnimation = cleanupAnimation;

            // 현재 위치로 이동하는 버튼 추가
            addLocationButton(mapInstance, center);

            // 매장 데이터 로드
            setTimeout(() => {
                // 컴포넌트가 마운트된 상태인지 다시 확인
                if (window.storeMapUnmounted) return;

                loadStores(mapInstance);
            }, 100);
        } catch (error) {
            // 컴포넌트가 마운트된 상태인지 확인
            if (window.storeMapUnmounted) return;

            showMessage('지도를 초기화하는 데 문제가 발생했습니다. 새로고침 후 다시 시도해주세요.', true);
        }
    };

    // 현재 위치로 이동하는 버튼 추가
    const addLocationButton = (mapInstance, initialLocation) => {
        // 버튼 DOM 요소 생성
        const locationButtonDiv = document.createElement('div');
        locationButtonDiv.className = 'location-button';

        // 위치 아이콘 SVG (FontAwesome 사용 대신 직접 SVG 추가)
        locationButtonDiv.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="18" height="18" fill="var(--main-color)">
                <path d="M256 0c-17.7 0-32 14.3-32 32V66.7C137.9 81.9 81.9 137.9 66.7 224H32c-17.7 0-32 14.3-32 32s14.3 32 32 32H66.7C81.9 374.1 137.9 430.1 224 445.3V480c0 17.7 14.3 32 32 32s32-14.3 32-32V445.3C374.1 430.1 430.1 374.1 445.3 288H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H445.3C430.1 137.9 374.1 81.9 288 66.7V32c0-17.7-14.3-32-32-32zM256 400c-79.5 0-144-64.5-144-144s64.5-144 144-144s144 64.5 144 144s-64.5 144-144 144z"/>
            </svg>
        `;
        locationButtonDiv.title = '내 위치로 이동';

        // 버튼 스타일 설정
        locationButtonDiv.style.cssText = `
            position: absolute;
            bottom: 30px;
            right: 10px;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background-color: white;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
            cursor: pointer;
            z-index: 10;
            display: flex;
            align-items: center;
            justify-content: center;
        `;

        // 커스텀 컨트롤을 지도에 추가
        const mapContainer = document.getElementById('map');
        mapContainer.appendChild(locationButtonDiv);

        // 버튼 클릭 이벤트 처리
        locationButtonDiv.addEventListener('click', () => {
            // 저장된 사용자 위치 또는 초기 위치로 지도 이동
            const targetLocation = userLocation || initialLocation;

            // 위치로 이동하고 애니메이션 효과 추가
            mapInstance.setCenter(targetLocation);
            mapInstance.setLevel(5); // 줌 레벨 설정 (필요에 따라 조정)

            // 위치 표시 애니메이션 재생성 
            if (window.cleanupLocationAnimation) {
                window.cleanupLocationAnimation();
            }
            const cleanupAnimation = createPulsingAnimation(mapInstance, targetLocation);
            window.cleanupLocationAnimation = cleanupAnimation;

            // 버튼 클릭 시 시각적 피드백
            locationButtonDiv.style.backgroundColor = '#f0f0f0';
            setTimeout(() => {
                locationButtonDiv.style.backgroundColor = 'white';
            }, 200);
        });
    };

    const createStoreMarkerImage = () => {
        // 마커 이미지를 생성합니다
        const imageSrc = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
            <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M12 0C5.37 0 0 5.37 0 12C0 20.25 12 32 12 32C12 32 24 20.25 24 12C24 5.37 18.63 0 12 0ZM12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16Z" fill="#FE531F"/>
            </svg>
        `)}`;

        const imageSize = new window.kakao.maps.Size(24, 32); // 마커 이미지의 크기
        const imageOption = { offset: new window.kakao.maps.Point(12, 32) }; // 마커 좌표에 일치시킬 이미지 내의 좌표

        return new window.kakao.maps.MarkerImage(imageSrc, imageSize, imageOption);
    };

    // 위치 핸들러 관련 부분을 수정합니다 - useEffect 내부
    useEffect(() => {
        // iOS에서 위치 정보를 받기 위한 전역 함수 정의
        window.handleLocationFromIOS = (latitude, longitude) => {
            devLog('📍 iOS에서 위치 정보 수신:', latitude, longitude);

            // 타임아웃 취소
            if (window.clearIOSLocationTimeout) {
                window.clearIOSLocationTimeout();
            }

            if (!latitude || !longitude) {
                console.error('유효하지 않은 위치 정보:', latitude, longitude);
                return;
            }

            // 컴포넌트가 마운트된 상태인지 확인 (cleanup 함수 실행 여부로 판단)
            if (window.storeMapUnmounted) {
                devLog('컴포넌트가 이미 언마운트되어 위치 정보를 무시합니다.');
                return;
            }

            const container = document.getElementById('map');
            // container가 없으면 컴포넌트가 언마운트된 것으로 간주
            if (!container) {
                devLog('map 요소가 존재하지 않아 위치 정보를 무시합니다.');
                return;
            }

            const position = new window.kakao.maps.LatLng(latitude, longitude);

            // 사용자 위치 업데이트
            setUserLocation(position);

            // 맵이 이미 초기화되었는지 확인
            if (map) {
                devLog('지도가 이미 초기화되어 있어 새 위치로 이동합니다.');
                map.setCenter(position);
                createPulsingAnimation(map, position);
            } else {
                devLog('iOS 위치 정보로 지도를 초기화합니다.');
                showMessage('iOS에서 전달 받은 위치 정보로 지도를 초기화합니다.');
                initializeMap(container, position);
            }
        };

        // 안드로이드에서 위치 정보를 받기 위한 전역 함수 정의
        window.handleLocationFromAndroid = (latitude, longitude) => {
            devLog('📍 안드로이드에서 위치 정보 수신:', latitude, longitude);

            // 타임아웃 취소
            if (window.clearAndroidLocationTimeout) {
                window.clearAndroidLocationTimeout();
            }

            if (!latitude || !longitude) {
                console.error('유효하지 않은 위치 정보:', latitude, longitude);
                return;
            }

            // 컴포넌트가 마운트된 상태인지 확인 (cleanup 함수 실행 여부로 판단)
            if (window.storeMapUnmounted) {
                devLog('컴포넌트가 이미 언마운트되어 위치 정보를 무시합니다.');
                return;
            }

            const container = document.getElementById('map');
            // container가 없으면 컴포넌트가 언마운트된 것으로 간주
            if (!container) {
                devLog('map 요소가 존재하지 않아 위치 정보를 무시합니다.');
                return;
            }

            const position = new window.kakao.maps.LatLng(latitude, longitude);

            // 사용자 위치 업데이트
            setUserLocation(position);

            // 맵이 이미 초기화되었는지 확인
            if (map) {
                devLog('지도가 이미 초기화되어 있어 새 위치로 이동합니다.');
                map.setCenter(position);
                createPulsingAnimation(map, position);
            } else {
                devLog('안드로이드 위치 정보로 지도를 초기화합니다.');
                showMessage('안드로이드에서 전달 받은 위치 정보로 지도를 초기화합니다.');
                initializeMap(container, position);
            }
        };

        // 앱에서 실행되고 있는지 여부를 확인하기 위한 함수
        window.handleIsOurAppWebView = (isApp) => {
            devLog('앱에서 실행 중 여부:', isApp);
            if (isApp) {
                // iOS 앱에서 실행 중임을 알림
                setStatusMessage('iOS 앱에서 위치 정보를 요청합니다...');
                // BRIDGE 메시지 핸들러로 위치 요청 메시지 전송
                try {
                    window.webkit.messageHandlers.BRIDGE.postMessage('requestLocation');
                    devLog('iOS에 위치 정보 요청을 보냈습니다.');
                } catch (e) {
                    console.error('iOS 메시지 전송 중 오류:', e);
                    // 오류 발생 시 일반 웹 방식으로 위치 요청
                    requestLocationPermission(document.getElementById('map'), new window.kakao.maps.LatLng(37.5665, 126.9780));
                }
            }
        };

        // 앱 실행 여부 확인 시도
        try {
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.isOurAppWebView) {
                window.webkit.messageHandlers.isOurAppWebView.postMessage('isAppRunning');
                devLog('앱 실행 여부 확인 요청 전송');
            }
        } catch (e) {
            devLog('브라우저에서 실행 중입니다.');
        }

        // 컴포넌트 마운트 상태 추적을 위한 플래그 설정
        window.storeMapUnmounted = false;

        return () => {
            // 컴포넌트 언마운트 플래그 설정
            window.storeMapUnmounted = true;

            // 전역 함수 제거 전에 실행되고 있는 작업이 있으면 중단
            if (window.clearIOSLocationTimeout) {
                window.clearIOSLocationTimeout();
                delete window.clearIOSLocationTimeout;
            }
            if (window.clearAndroidLocationTimeout) {
                window.clearAndroidLocationTimeout();
                delete window.clearAndroidLocationTimeout;
            }

            // 전역 함수 제거
            delete window.handleLocationFromIOS;
            delete window.handleLocationFromAndroid;
            delete window.handleIsOurAppWebView;

            // 애니메이션 정리 함수 호출
            if (window.cleanupLocationAnimation) {
                devLog('위치 표시 애니메이션 정리');
                window.cleanupLocationAnimation();
                delete window.cleanupLocationAnimation;
            }
        };
    }, []);

    // 위치 권한 요청 함수 수정
    const requestLocationPermission = async (container, defaultCenter) => {
        try {
            showMessage('위치 정보를 요청하는 중...');

            // 안드로이드 앱 환경에서 실행 중인지 확인
            if (window.BRIDGE && window.BRIDGE.requestLocation) {
                showMessage('안드로이드 앱에서 위치 정보를 요청합니다...');
                window.BRIDGE.requestLocation();

                // 10초 타임아웃 설정 - 안드로이드에서 응답이 없을 경우를 대비
                let locationTimeout = setTimeout(() => {
                    // 컴포넌트가 마운트된 상태인지 확인
                    if (window.storeMapUnmounted) return;

                    proceedWithWebLocationRequest();
                }, 10000);

                // 전역 변수로 타임아웃 ID 저장해서 안드로이드에서 위치 정보가 오면 취소할 수 있게 함
                window.clearAndroidLocationTimeout = () => {
                    clearTimeout(locationTimeout);
                    delete window.clearAndroidLocationTimeout;
                };

                return; // 안드로이드 앱에 요청을 보냈으니 여기서 함수 종료
            }

            // iOS 앱 환경에서 실행 중인지 확인 시도
            try {
                if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.BRIDGE) {
                    window.webkit.messageHandlers.BRIDGE.postMessage('requestLocation');
                    showMessage('iOS 앱에서 위치 정보를 요청합니다...');

                    // 10초 타임아웃 설정 - iOS에서 응답이 없을 경우를 대비
                    let locationTimeout = setTimeout(() => {
                        // 컴포넌트가 마운트된 상태인지 확인
                        if (window.storeMapUnmounted) return;

                        proceedWithWebLocationRequest();
                    }, 10000);

                    // 전역 변수로 타임아웃 ID 저장해서 iOS에서 위치 정보가 오면 취소할 수 있게 함
                    window.clearIOSLocationTimeout = () => {
                        clearTimeout(locationTimeout);
                        delete window.clearIOSLocationTimeout;
                    };

                    return; // iOS 앱에 요청을 보냈으니 여기서 함수 종료
                }
            } catch (e) {
                // iOS 앱 환경이 아닌 경우 웹 방식으로 진행
            }

            // 일반 웹 방식으로 위치 요청 진행
            proceedWithWebLocationRequest();

            // 웹 방식 위치 요청 함수
            function proceedWithWebLocationRequest() {
                // 컴포넌트가 마운트된 상태인지 확인
                if (window.storeMapUnmounted) return;

                // 기존 위치 요청 코드를 여기로 이동
                let locationTimeout = null;

                // 10초 후에도 위치를 가져오지 못하면 기본 위치로 초기화
                locationTimeout = setTimeout(() => {
                    // 컴포넌트가 마운트된 상태인지 확인
                    if (window.storeMapUnmounted) return;

                    if (!map) {
                        showMessage('위치 정보를 가져오는 데 시간이 오래 걸려 기본 위치로 지도를 표시합니다.');
                        initializeMap(container, defaultCenter);
                    }
                }, 10000);

                // 위치 정보 요청
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            // 컴포넌트가 마운트된 상태인지 확인
                            if (window.storeMapUnmounted) {
                                clearTimeout(locationTimeout);
                                return;
                            }

                            clearTimeout(locationTimeout);

                            const currentPos = new window.kakao.maps.LatLng(
                                position.coords.latitude,
                                position.coords.longitude
                            );

                            // 사용자 위치 업데이트
                            setUserLocation(currentPos);

                            // 성공 메시지 표시 후 지도 초기화
                            showMessage('위치 정보 획득 성공! 지도를 초기화합니다.');

                            // setTimeout으로 약간의 지연을 주어 메시지가 표시된 후 지도 초기화 진행
                            setTimeout(() => {
                                // 컴포넌트가 마운트된 상태인지 다시 확인
                                if (window.storeMapUnmounted) return;

                                initializeMap(container, currentPos);
                            }, 300);
                        },
                        (error) => {
                            // 컴포넌트가 마운트된 상태인지 확인
                            if (window.storeMapUnmounted) {
                                clearTimeout(locationTimeout);
                                return;
                            }

                            clearTimeout(locationTimeout);

                            let errorMessage = '위치 정보를 가져올 수 없어 기본 위치로 지도를 표시합니다.';

                            if (error.code === error.PERMISSION_DENIED) {
                                errorMessage = '위치 정보 접근이 거부되었습니다. 기본 위치로 지도를 표시합니다.';
                            } else if (error.code === error.POSITION_UNAVAILABLE) {
                                errorMessage = '위치 정보를 사용할 수 없습니다. 기본 위치로 지도를 표시합니다.';
                            } else if (error.code === error.TIMEOUT) {
                                errorMessage = '위치 정보 요청 시간이 초과되었습니다. 기본 위치로 지도를 표시합니다.';
                            }

                            showMessage(errorMessage, true);
                            // 오류가 발생해도 지도는 초기화
                            initializeMap(container, defaultCenter);
                        },
                        {
                            enableHighAccuracy: true,
                            timeout: 10000,
                            maximumAge: 0
                        }
                    );
                } else {
                    clearTimeout(locationTimeout);
                    showMessage('이 브라우저에서는 위치 정보를 사용할 수 없습니다.', true);
                    initializeMap(container, defaultCenter);
                }
            }
        } catch (error) {
            // 컴포넌트가 마운트된 상태인지 확인
            if (window.storeMapUnmounted) return;

            showMessage('위치 정보 처리 중 오류가 발생했습니다. 기본 위치로 지도를 표시합니다.', true);
            initializeMap(container, defaultCenter);
        }
    };

    useEffect(() => {
        devLog('StoreMap 컴포넌트 마운트, 카카오맵 초기화 시작');
        const container = document.getElementById('map');
        const defaultCenter = new window.kakao.maps.LatLng(37.5665, 126.9780); // 서울시청

        // 이미 스크립트가 로드되어 있는지 확인
        if (window.kakao && window.kakao.maps) {
            devLog('카카오맵 API 이미 로드됨, 바로 위치 권한 요청');
            setIsLoading(false);
            showMessage('위치 정보를 요청합니다...');

            // 일부 iOS 환경에서는 setTimeout으로 약간의 지연 후 실행하는 것이 도움이 됨
            setTimeout(() => {
                requestLocationPermission(container, defaultCenter);
            }, 1000);
            return;
        }

        devLog('카카오맵 API 로드 시작');
        showMessage('카카오맵 API를 불러오는 중...');

        const script = document.createElement('script');
        script.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=${process.env.REACT_APP_KAKAO_MAP_KEY}`;
        script.async = true;

        script.onload = () => {
            devLog('카카오맵 스크립트 로드 완료');

            // iOS Safari 특성 상 window.kakao가 즉시 사용 가능하지 않을 수 있음
            const waitForKakaoMaps = setInterval(() => {
                if (window.kakao && window.kakao.maps) {
                    devLog('카카오맵 객체 사용 가능');
                    clearInterval(waitForKakaoMaps);

                    window.kakao.maps.load(() => {
                        devLog('카카오맵 API 로드 완료');
                        setIsLoading(false);
                        showMessage('카카오맵 로드 완료, 위치 정보를 요청합니다...');

                        // 일부 iOS 환경에서는 setTimeout으로 약간의 지연 후 실행하는 것이 도움이 됨
                        setTimeout(() => {
                            requestLocationPermission(container, defaultCenter);
                        }, 1000);
                    });
                }
            }, 100);

            // 10초 후에도 로드되지 않으면 기본 위치로 초기화
            setTimeout(() => {
                if (isLoading) {
                    devLog('카카오맵 로드 타임아웃, 기본 초기화');
                    clearInterval(waitForKakaoMaps);
                    setIsLoading(false);
                    // iOS 디버깅용 alert 추가
                    // alert('[디버깅] 카카오맵 API 로드 타임아웃: window.kakao.maps 객체를 찾을 수 없습니다.');
                    showMessage('지도를 불러오는 데 시간이 오래 걸려 기본 설정으로 초기화합니다.', true);
                    initializeMap(container, defaultCenter);
                }
            }, 10000);
        };

        script.onerror = (e) => {
            console.error('카카오맵 API 로드 실패:', e);
            // iOS 디버깅용 alert 추가
            // alert(`[디버깅] 카카오맵 API 로드 실패: ${e ? e.type : '알 수 없는 오류'}`);
            showMessage('지도 API를 불러오는 데 실패했습니다. 네트워크 연결을 확인해주세요.', true);
            setIsLoading(false);
        };

        document.head.appendChild(script);

        return () => {
            devLog('StoreMap 컴포넌트 언마운트');
            // 애니메이션 정리 함수 호출
            if (window.cleanupLocationAnimation) {
                devLog('위치 표시 애니메이션 정리');
                window.cleanupLocationAnimation();
                delete window.cleanupLocationAnimation;
            }

            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, []);

    const loadStores = (mapInstance) => {
        if (!mapInstance) return;

        requester.findAllByConfirmYnIsTrue((result) => {
            if (result.code === 0 && result.data) {
                setStores(result.data);

                const storeMarkerImage = createStoreMarkerImage();

                result.data.forEach(store => {
                    // 위도 경도 데이터가 있는 매장만 표시
                    if (store.latitudeByX && store.longitudeByY) {
                        try {
                            const lat = parseFloat(store.latitudeByX);
                            const lng = parseFloat(store.longitudeByY);

                            // 유효한 좌표값인지 확인
                            if (isNaN(lat) || isNaN(lng)) return;

                            const marker = new window.kakao.maps.Marker({
                                position: new window.kakao.maps.LatLng(lat, lng),
                                map: mapInstance,
                                image: storeMarkerImage
                            });

                            window.kakao.maps.event.addListener(marker, 'click', () => {
                                setSelectedStore(store);
                                setShowModal(true);
                            });
                        } catch (e) {
                            // 개별 마커 생성 실패는 전체 지도에 영향을 주지 않도록 처리
                        }
                    }
                });
            } else {
                showMessage('매장 정보를 불러오는 데 실패했습니다.', true);
            }
        });
    };

    const handleSearch = () => {
        if (searchKeyword.trim()) {
            requester.searchByStoreName({ storeName: searchKeyword }, (result) => {
                if (result.code === 0) {
                    setStores(result.data);
                    // 검색된 매장들의 마커 표시
                    if (map) {
                        map.setLevel(5);
                        if (result.data.length > 0 && result.data[0].latitudeByX && result.data[0].longitudeByY) {
                            map.setCenter(new window.kakao.maps.LatLng(
                                parseFloat(result.data[0].latitudeByX),
                                parseFloat(result.data[0].longitudeByY)
                            ));
                        }
                    }
                }
            });
        }
    };

    // 위치 재요청 핸들러
    const handleRetryLocation = () => {
        const container = document.getElementById('map');
        const defaultCenter = new window.kakao.maps.LatLng(37.5665, 126.9780);
        setLocationError(false);
        requestLocationPermission(container, defaultCenter);
    };

    // 메시지 표시 함수 (alert 대신 사용)
    const showMessage = (message, isError = false) => {
        devLog(`[상태 메시지] ${message}`);

        if (message === '') {
            // 메시지를 지우는 경우
            setStatusMessage('');
            if (!isError) {
                setLocationError(false);
            }
        } else {
            // 메시지를 설정하는 경우
            setStatusMessage(message);
            if (isError) {
                setLocationError(true);
            }
        }
    };

    return (
        <div style={{ height: '100%', position: 'relative' }}>
            {/* <Dummy height={"56px"} />
            <div style={{
                position: 'absolute',
                top: '56px',
                left: 0,
                right: 0,
                padding: '12px 16px',
                backgroundColor: '#fff',
                zIndex: 10,
                display: 'flex',
                gap: '8px'
            }}>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    padding: '0 12px'
                }}>
                    <FontAwesomeIcon 
                        icon={fas.faSearch} 
                        style={{ color: '#999', marginRight: '8px' }} 
                    />
                    <input
                        type="text"
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="매장 검색"
                        style={{
                            border: 'none',
                            backgroundColor: 'transparent',
                            flex: 1,
                            height: '40px',
                            fontSize: '15px'
                        }}
                    />
                </div>
            </div> */}
            <div id="map" style={{ width: '100%', height: '100%' }}></div>
            {(isLoading || statusMessage) && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1000,
                    textAlign: 'center',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    padding: '15px 20px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    maxWidth: '80%'
                }}>
                    <p style={{ fontSize: '16px', fontWeight: '500', margin: '0' }}>
                        {statusMessage || '지도를 불러오는 중...'}
                    </p>
                </div>
            )}
            {locationError && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1000,
                    textAlign: 'center',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    padding: '20px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}>
                    <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '15px' }}>
                        위치 정보를 가져오는 데 문제가 발생했습니다.
                    </p>
                    <button
                        onClick={() => {
                            const container = document.getElementById('map');
                            const defaultCenter = new window.kakao.maps.LatLng(37.5665, 126.9780);
                            setLocationError(false);
                            setStatusMessage('위치 정보를 다시 요청합니다...');
                            requestLocationPermission(container, defaultCenter);
                        }}
                        style={{
                            backgroundColor: 'var(--main-color)',
                            color: '#fff',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer'
                        }}
                    >
                        위치 권한 다시 요청하기
                    </button>
                </div>
            )}
            {selectedStore && !showModal && (
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#fff',
                    borderTopLeftRadius: '16px',
                    borderTopRightRadius: '16px',
                    padding: '20px',
                    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)'
                }}
                    onClick={() => setShowModal(true)}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ flex: 1 }}>
                            <P size={18} weight={600}>{selectedStore.storeName}</P>
                            <P size={14} color="#666" style={{ marginTop: '4px' }}>
                                {selectedStore.distance ? `${selectedStore.distance}m` : '영업중'}
                            </P>
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/ws/${id}/mobile/storeList`);
                            }}
                            style={{
                                backgroundColor: 'var(--main-color)',
                                color: '#fff',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer'
                            }}
                        >
                            바로가기
                        </button>
                    </div>
                </div>
            )}
            {showModal && (
                <SimpleStoreDetailModal
                    store={selectedStore}
                    isMapView={true}
                    userLocationProp={userLocation ? {
                        latitude: userLocation.getLat(),
                        longitude: userLocation.getLng()
                    } : null}
                    onClose={() => {
                        setShowModal(false);
                        setSelectedStore(null);
                    }}
                />
            )}
        </div>
    );
}

export default StoreMap; 